import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  generateDictionaries,
  getNameByCode,
} from "../../utils/conversion-directory-arkgis";
import { LayersDataTypes } from "../../types/layers-data-types";
import { Layer } from "../../interfaces/layer";
import { FiltersState } from "./filters";
import { LandPlot } from "../../interfaces/card";
import { LayerType } from "../../types/layer-type";
import {
  CardKrookies,
  ConvertedKrookie,
  ConvertedKrookieData,
  FormDataEditDetails,
  HistoryLog,
  KrookieDataResponse,
  KrookieStatus,
  KrookieStatusName,
  ResponseContour,
} from "../../interfaces/krookie";
import Polygon from "@arcgis/core/geometry/Polygon";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import {
  transformInvestmentOpportunity,
  transformPlot,
} from "../../utils/helpers-for-filter";
import { planarArea } from "@arcgis/core/geometry/geometryEngine";
import {
  convertPlotsToCoordinatesTable,
  ConvertPolygonsToCoordinatesTable,
  convertPolygonToString,
  ConvertToLines,
  getSpatialByWkid,
  isPolygon,
} from "../../utils/master-plan-helper";
import { AppDispatch, RootState } from "../store";
import {
  getDictionaries,
  GetKrookieCoordinates,
  getKrookiesInfo,
  GetPreviewKrookie,
} from "../api";
import {
  createEmptyFormData,
  createEmptyLandPlot,
  safeParse,
  transformToKrookiePlot,
} from "../../utils/krookie-helpers";
import { editFeaturesOnMap } from "../actions/master-plan-modification";
import { formatDateWithoutTime } from "../../utils/formatDate";
import { v4 as uuidv4 } from "uuid";

interface Drafter {
  name: string;
  date: Date;
}

export type Points = Array<{
  index: number;
  north: string | null;
  east: string | null;
  id: string;
  err?: boolean;
}>;

export enum FormationTypeNames {
  Create = "Create",
  Edit = "Edit",
  Merge = "Merge",
  Divide = "Divide",
}

export enum KrookieFormationTypeId {
  "Create" = 1,
  "Edit",
  "Merge",
  "Divide",
}

interface HistoryLogEntry {
  revision: string;
  date: string;
  drafter: string;
  comment: string;
}

export type LandPlotsCoordinatesBlock = Array<{
  PlotId: number;
  wkid?: number;
  contours: Contours;
  area?: string;
}>;
export type Contours = Array<{ id: string; points: Points; area: string }>;
export interface KrookiePlot extends LandPlot {
  contours?: Contours;
  area: string;
  newGeometry: Polygon | null;
  spatialReference: string;
  stringGeometry?: {
    rings: string[][][];
    wkid: number;
  };
}

export interface Screenshot {
  scale: number;
  url: string;
}

export interface LogComment {
  [krookieId: number]: {
    [commentId: number]: string;
  };
}

interface MasterPlanModificationState {
  drafter: Drafter | null;
  krookiePlots: KrookiePlot[];
  data: any[];
  filteredData: any[];
  filters: FiltersState;
  loading: boolean;
  error: string | null;
  citiesEn: Map<number, string>;
  citiesAr: Map<number, string>;
  selectedCity?: number | null;
  selectedPlots: LandPlot[];
  selectedFormationType?: FormationTypeNames | null;
  formDataEditDetails: Record<number, FormDataEditDetails>;
  screenshots: Record<number, Screenshot[]>;
  rawKrookieData: Record<number, KrookieDataResponse>;
  convertedKrookieData: ConvertedKrookieData;
  logComment: LogComment;
  landUse: Map<number, string>;
  phase: Map<number, string>;
  status: Map<string, string>;
  locationStatusCode: Map<number, string>;
  dictionaries: { [key: string]: { [key: string]: string } };
  historyLog: HistoryLogEntry[];
  isLoaded: boolean;
  coordinatesData: LandPlotsCoordinatesBlock | null;
  blockNavigationMasterPlan: boolean;
}

const initialState: MasterPlanModificationState = {
  drafter: null,
  krookiePlots: [],
  data: [],
  selectedPlots: [],
  selectedCity: null,
  formDataEditDetails: {},
  screenshots: {},
  rawKrookieData: {},
  convertedKrookieData: {
    krookies: {},
    oldPlots: [],
  },
  selectedFormationType: null,
  filteredData: [],
  filters: {},
  loading: false,
  error: null,
  citiesEn: new Map<number, string>(),
  citiesAr: new Map<number, string>(),
  landUse: new Map<number, string>(),
  phase: new Map<number, string>(),
  status: new Map<string, string>(),
  locationStatusCode: new Map<number, string>(),
  dictionaries: {},
  historyLog: [],
  logComment: {},
  isLoaded: false,
  coordinatesData: null,
  blockNavigationMasterPlan: false,
};

export const fetchMasterPlanModificationData = createAsyncThunk(
  "masterPlanModification/fetchMasterPlanModificationData",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const layers = state.map.layers.filter(
        (layer: Layer) =>
          [
            LayersDataTypes.Plots,
            LayersDataTypes.InvestmentOpportunities,
          ].includes(layer.dataType) &&
          layer.type === LayerType.ArcgisFeatureServer,
      );

      let allFeatures: any[] = [];
      let dictionaries: { [key: string]: { [key: string]: string } } = {};
      for (const layer of layers) {
        const featureLayer = new FeatureLayer({
          url: layer.url!,
          definitionExpression: layer.style2D?.query?.replace(
            " and LandUse <> 3",
            "",
          ),
        });
        const query = featureLayer.createQuery();
        const queryFeatures = await featureLayer.queryFeatures(query);
        if (featureLayer.fields) {
          dictionaries = {
            ...dictionaries,
            ...generateDictionaries(featureLayer.fields),
          };
        }
        const features = queryFeatures.features
          .map<any>((feature) => ({
            ...feature.attributes,
            geometry: feature.geometry,
            layer: layer,
            objectId: feature.getObjectId(),
            rawAttributes: feature.attributes,
          }))
          .map<any>((feature) => {
            const result =
              layer.dataType === LayersDataTypes.Plots
                ? { ...transformPlot(feature, dictionaries) }
                : { ...transformInvestmentOpportunity(feature, dictionaries) };
            return { ...result, attributes: feature.rawAttributes };
          });
        allFeatures = allFeatures.concat(features);
      }
      return {
        data: allFeatures,
        citiesEn: new Map(
          Object.entries(dictionaries["estateEn"] || {}).map(([key, value]) => [+key, value]),
        ),
        citiesAr: new Map(
          Object.entries(dictionaries["estateAr"] || {}).map(([key, value]) => [+key, value]),
        ),
        landUse: new Map(
          Object.entries(dictionaries["Landuse"] || {}).map(([key, value]) => [
            +key,
            value,
          ]),
        ),
        phase: new Map(
          Object.entries(dictionaries["parent_location_Serial"] || {}).map(
            ([key, value]) => [+key, value],
          ),
        ),
        status: new Map(
          Object.entries(dictionaries["status"] || {}).map(([key, value]) => [
            key,
            value,
          ]),
        ),
        locationStatusCode: new Map(
          Object.entries(dictionaries["location_status_code"] || {}).map(
            ([key, value]) => [+key, value],
          ),
        ),
      };
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to load Master Plan Modification data",
      );
    }
  },
);

export const fetchKrookieData = createAsyncThunk(
  "masterPlanModification/fetchKrookieData",
  async (
    {
      sessionId,
      status,
      krookieId,
    }: { sessionId: number; status: KrookieStatusName; krookieId?: number },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const { layers, arcgisTokens } = state.map;
      const plotLayer = layers.find(
        (layer: any) => layer.dataType === LayersDataTypes.Plots && layer.url,
      );

      if (!plotLayer) return rejectWithValue("Plot layer not found");

      if (!arcgisTokens || Object.keys(arcgisTokens).length === 0)
        return rejectWithValue("ArcGIS token is missing");

      const [dictionaries, res] = await Promise.all([
        getDictionaries(plotLayer, arcgisTokens),
        getKrookiesInfo(sessionId),
      ]);

      if (!dictionaries.cities.length || !dictionaries.landUse.length)
        return rejectWithValue("Failed to load dictionaries");
      const { krookies, oldPlots } = res;

      const targetKrookie = krookieId
        ? krookies.find((k) => k.id === krookieId)
        : null;
      const targetFormationType = targetKrookie?.krookieFormationTypeId;

      const reorderedKrookies = krookies
        .filter(
          (krookie) =>
            !targetFormationType ||
            krookie.krookieFormationTypeId === targetFormationType,
        )
        .sort((a, b) => {
          if (a.id === krookieId) return -1;
          if (b.id === krookieId) return 1;
          return 0;
        });

      const convertedKrookies: Record<number, ConvertedKrookie> = {};

      for (let krookie of reorderedKrookies) {
        let documentImage = null;
        if (status === KrookieStatusName.Approved) {
          try {
            const blobImage = await GetPreviewKrookie(krookie.id);
            documentImage = URL.createObjectURL(blobImage);
          } catch (error) {
            return rejectWithValue(error);
          }
        }

        const convertedHistoryLogs: HistoryLog[] = krookie.historyLogs.map(
          (log: any) => ({
            ...log,
            attributes: safeParse(log.attributes),
          }),
        );

        const latestLog = convertedHistoryLogs[convertedHistoryLogs.length - 1];

        const revision = latestLog
          ? `${formatDateWithoutTime(latestLog.changedDateTime)}_${latestLog.changeNum}`
          : "";

        const convertedGeometry: Polygon = safeParse(krookie.geometry);

        const lastNeedApprovalLog = [...convertedHistoryLogs]
          .reverse()
          .find(
            (log) =>
              log.attributes?.KrookieStatusId === KrookieStatus.NeedApproval ||
              log.attributes?.KrookieStatusId === KrookieStatus.Project,
          );

        const drafterName = lastNeedApprovalLog
          ? lastNeedApprovalLog.userName
          : "";
        const signatureDrafter = lastNeedApprovalLog
          ? lastNeedApprovalLog.changedDateTime
          : "";

        const currentObject = oldPlots?.[0]
          ? state.masterPlanModification.data.find(
              (plot: LandPlot) => plot.objectId === oldPlots[0].plotId,
            )
          : null;

        const qrCodeLinkValue =
          krookie.krookieFormationTypeId !== KrookieFormationTypeId.Edit ||
          !currentObject
            ? ""
            : `${window.location.origin}${process.env.PUBLIC_URL}/search/plt?city=${currentObject.estateEn}&res=true&fid=${currentObject.fid}`;
        const converted = {
          id: krookie.id,
          sessionId: krookie.krookieSessionId,
          statusId: krookie.status.id,
          cityEn: {
            displayValue: getNameByCode(dictionaries.cities, krookie.cityId),
            value: krookie.cityId,
          },
          cityAr: getNameByCode(dictionaries.estateAr, krookie.cityId),
          phase: {
            displayValue: getNameByCode(dictionaries.phase, krookie.phaseId),
            value: krookie.phaseId,
          },
          plotNumber: krookie.newPlotNum.toString(),
          oldPlotNumber: oldPlots.map((plot) => plot.plotNum).join(", "),
          streetNumber: krookie.streetNum,
          landUse: {
            displayValue: getNameByCode(dictionaries.landUse, krookie.landUse),
            value: krookie.landUse,
          },
          status: {
            displayValue: getNameByCode(
              dictionaries.status,
              krookie.plotStatus,
            ),
            value: krookie.plotStatus,
          },
          locationStatusCode: {
            displayValue: getNameByCode(
              dictionaries.locationStatusCode,
              krookie.plotLocationStatusCode,
            ),
            value: krookie.plotLocationStatusCode,
          },
          isInvestmentOpportunity: Boolean(krookie.plotIsInvOp),
          buildArea: krookie.buildArea.toString(),
          area: krookie.area.toString(),
          height: krookie.maxHeight,
          front: krookie.frontSetback.toString(),
          back: krookie.backSetback.toString(),
          side: krookie.sideSetback.toString(),
          qrCodeLink: qrCodeLinkValue,
          notes: krookie.notes || [],
          serial: krookie.serialNum,
          previousKrookieCode:
            krookie.previousKrookieNum && krookie.previousKrookieNum !== "null"
              ? krookie.previousKrookieNum
              : "",
          revision: revision,
          wgs: getSpatialByWkid(convertedGeometry.spatialReference.wkid),
          drafterName: drafterName,
          directorName: "",
          signatureDrafter: formatDateWithoutTime(signatureDrafter),
          signatureDirector: "",
          tableData: ConvertToLines(convertedGeometry) ?? [],
          historyLog: convertedHistoryLogs,
          geometry: convertedGeometry,
          screenshots: {
            firstScreenshot: {
              scale: krookie.minScale,
              url: "data:image/png;;base64," + krookie.minScaleScreenShot,
            },
            secondScreenshot: {
              scale: krookie.maxScale,
              url: "data:image/png;;base64," + krookie.maxScaleScreenShot,
            },
          },
          userPosition: "",
          documentImage,
          krookieFormationTypeId: krookie.krookieFormationTypeId,
        };

        let stateKey: number;

        if (krookie.newPlotId) {
          stateKey = krookie.newPlotId;
        } else {
          const formationType = krookie.krookieFormationTypeId;

          switch (formationType) {
            case KrookieFormationTypeId.Create:
              stateKey = -krookie.id;
              break;
            case KrookieFormationTypeId.Edit:
              stateKey =
                oldPlots.length > 0
                  ? oldPlots[oldPlots.length - 1].plotId
                  : -krookie.id;
              break;
            case KrookieFormationTypeId.Merge:
              stateKey = -krookie.id;
              break;
            case KrookieFormationTypeId.Divide:
              stateKey = -krookie.id;
          }
        }

        convertedKrookies[stateKey] = converted;
      }

      return {
        rawKrookieData: res,
        convertedKrookieData: {
          krookies: convertedKrookies,
          oldPlots: oldPlots,
        },
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchCoordinatesData = createAsyncThunk(
  "masterPlanModification/fetchCoordinatesData",
  async (
    { krookieId, PlotId }: { krookieId: number; PlotId: number },
    { rejectWithValue },
  ) => {
    try {
      const res = await GetKrookieCoordinates(krookieId);

      const normalizedContours: Contours =
        res.contours?.map((contour: ResponseContour) => ({
          ...contour,
          id: uuidv4(),
          points: contour.points?.map((point) => ({
            id: uuidv4(),
            index: point.index,
            north:
              point.x !== null && point.x !== undefined
                ? point.x.toString()
                : null,
            east:
              point.y !== null && point.y !== undefined
                ? point.y.toString()
                : null,
          })),
          area: "0",
        })) || [];

      const plotInfo: LandPlotsCoordinatesBlock = [
        {
          PlotId,
          wkid: res.wkid,
          contours: normalizedContours,
          area: "0",
        },
      ];

      const stringGeomMap = ConvertPolygonsToCoordinatesTable(plotInfo);
      const stringGeometry = stringGeomMap.get(PlotId);

      const [plotWithAreas] = convertPlotsToCoordinatesTable([
        { objectId: PlotId, stringGeometry },
      ]);

      return { normalizedData: [plotWithAreas] };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateConvertedKrookieDataAsync =
  ({ id, data }: { id: number; data: Partial<CardKrookies> }) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(updateConvertedKrookieData({ id, data }));
    return Promise.resolve();
  };

export const masterPlanModificationSlice = createSlice({
  name: "masterPlanModification",
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<number>) => {
      if (action.payload) {
        state.filteredData = state.data.filter(
          (item) => item.estateEn === action.payload,
        );
      }
    },
    setDrafter: (state, action: PayloadAction<Drafter>) => {
      state.drafter = action.payload;
    },
    setSelectedPlots: (state, action: PayloadAction<LandPlot[]>) => {
      state.selectedPlots = action.payload;
    },
    setKrookiePlots: (
      state,
      action: PayloadAction<
        LandPlot[] | { wkid: number; count?: number; append?: boolean }
      >,
    ) => {
      const payload = action.payload;
      if (Array.isArray(payload)) {
        state.krookiePlots = payload.map(transformToKrookiePlot);
      } else {
        const { wkid, count = 1, append = false } = payload;
        const cityId = state.selectedCity;

        if (!cityId) {
          return;
        }

        const existingNegativeIds = state.krookiePlots
          .map((plot) => plot.objectId)
          .filter((id) => id < 0);
        const minNegativeId =
          existingNegativeIds.length > 0 ? Math.min(...existingNegativeIds) : 0;
        const startId = minNegativeId < 0 ? minNegativeId - 1 : -1;

        const emptyPlots = Array.from({ length: count }, (_, index) =>
          createEmptyLandPlot(wkid, cityId, startId - index),
        );

        if (append) {
          state.krookiePlots = [
            ...state.krookiePlots,
            ...emptyPlots.map(transformToKrookiePlot),
          ];
        } else {
          state.krookiePlots = emptyPlots.map(transformToKrookiePlot);
        }
      }
    },
    deletePlot: (state, action: PayloadAction<number>) => {
      const objectId = action.payload;
      state.krookiePlots = state.krookiePlots.filter(
        (plot) => plot.objectId !== objectId,
      );
      delete state.formDataEditDetails[objectId];
      delete state.screenshots[objectId];
      delete state.logComment[objectId];
      delete state.rawKrookieData[objectId];
      delete state.convertedKrookieData.krookies[objectId];
    },
    setGeometries: (
      state,
      action: PayloadAction<{
        newGeometry: Map<number, Polygon>;
        stringGeometry?: Map<number, { rings: string[][][]; wkid: number }>;
        clearSelection?: boolean;
      }>,
    ) => {
      state.krookiePlots = state.krookiePlots.map((plot) => {
        {
          const geometry = action.payload.newGeometry.has(plot.objectId)
            ? action.payload.newGeometry.get(plot.objectId)!
            : null;
          const stringGeometry =
            action.payload.stringGeometry?.get(plot.objectId) ??
            (geometry ? convertPolygonToString(geometry) : plot.stringGeometry);
          return {
            ...plot,
            newGeometry: geometry,
            stringGeometry,
            area:
              geometry && isPolygon(geometry.rings) && geometry.spatialReference?.wkid
                ? planarArea(geometry, "square-meters").toFixed(2)
                : "0",
          };
        }
      });
    },
    resetNewGeometry: (state, action: PayloadAction<number | undefined>) => {
      const resetPlotId = action.payload;
      state.krookiePlots = state.krookiePlots.map((plot) => {
        if (resetPlotId === undefined || plot.objectId === resetPlotId) {
          const geometry = plot.geometry as Polygon;
          return {
            ...plot,
            newGeometry: geometry,
            stringGeometry: convertPolygonToString(geometry),
            area: planarArea(geometry, "square-meters").toFixed(2),
          };
        }
        return plot;
      });
    },
    setSelectedCity: (state, action: PayloadAction<number | null>) => {
      state.selectedCity = action.payload;
    },
    setSelectedFormationType: (
      state,
      action: PayloadAction<FormationTypeNames | null>,
    ) => {
      state.selectedFormationType = action.payload;
    },
    clearAllSelected: (state) => {
      state.selectedPlots = initialState.selectedPlots;
      state.krookiePlots = initialState.krookiePlots;
      state.selectedCity = initialState.selectedCity;
      state.screenshots = initialState.screenshots;
      state.formDataEditDetails = initialState.formDataEditDetails;
      state.selectedFormationType = initialState.selectedFormationType;
    },
    setFormDataEditDetails: (
      state,
      action: PayloadAction<{
        id: number;
        formData: Partial<FormDataEditDetails>;
      }>,
    ) => {
      const { id, formData } = action.payload;

      if (!state.formDataEditDetails[id]) {
        state.formDataEditDetails[id] = createEmptyFormData();
      }

      Object.assign(state.formDataEditDetails[id], formData);
    },
    setScreenshot: (
      state,
      action: PayloadAction<{
        id: number;
        screenshot: Screenshot;
      }>,
    ) => {
      const { id, screenshot } = action.payload;
      if (!state.screenshots[id]) {
        state.screenshots[id] = [];
      }

      state.screenshots[id].push(screenshot);
    },
    clearScreenshot: (state) => {
      state.screenshots = initialState.screenshots;
    },
    updateConvertedKrookieData: (
      state,
      action: PayloadAction<{
        id: number;
        data: Partial<ConvertedKrookie>;
      }>,
    ) => {
      const { id, data } = action.payload;

      if (!state.convertedKrookieData.krookies[id]) {
        state.convertedKrookieData.krookies[id] = {} as ConvertedKrookie;
      }

      Object.assign(state.convertedKrookieData.krookies[id], data);
    },
    setLogComment: (
      state,
      action: PayloadAction<{
        documentId: number;
        recordId: number;
        comment: string;
      }>,
    ) => {
      const { documentId, recordId, comment } = action.payload;

      if (!state.logComment[documentId]) {
        state.logComment[documentId] = {};
      }

      state.logComment[documentId][recordId] = comment;
    },
    setResetKrookieData: (state) => {
      state.rawKrookieData = initialState.rawKrookieData;
      state.convertedKrookieData = initialState.convertedKrookieData;
      state.formDataEditDetails = initialState.formDataEditDetails;
      state.screenshots = initialState.screenshots;
      state.logComment = initialState.logComment;
    },
    resetFormDataEditDetails: (state) => {
      state.formDataEditDetails = initialState.formDataEditDetails;
    },
    setBlockNavigation: (state, action: PayloadAction<boolean>) => {
      state.blockNavigationMasterPlan = action.payload;
    },
    setCoordinatesData: (
      state,
      action: PayloadAction<LandPlotsCoordinatesBlock | null>,
    ) => {
      state.coordinatesData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMasterPlanModificationData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchMasterPlanModificationData.fulfilled,
        (
          state,
          action: PayloadAction<{
            data: LandPlot[];
            citiesEn: Map<number, string>;
            citiesAr: Map<number, string>;
            landUse: Map<number, string>;
            phase: Map<number, string>;
            status: Map<string, string>;
            locationStatusCode: Map<number, string>;
          }>,
        ) => {
          state.data = action.payload.data;
          state.citiesEn = action.payload.citiesEn;
          state.citiesAr = action.payload.citiesAr;
          state.landUse = action.payload.landUse;
          state.phase = action.payload.phase;
          state.status = action.payload.status;
          state.locationStatusCode = action.payload.locationStatusCode;
          state.loading = false;
          state.isLoaded = true;
        },
      )
      .addCase(fetchMasterPlanModificationData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchKrookieData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchKrookieData.fulfilled,
        (
          state,
          action: PayloadAction<{
            rawKrookieData: any;
            convertedKrookieData: any;
          }>,
        ) => {
          state.rawKrookieData = action.payload.rawKrookieData;
          state.convertedKrookieData = action.payload.convertedKrookieData;
          state.logComment = {};
          state.loading = false;
        },
      )
      .addCase(fetchKrookieData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(editFeaturesOnMap, (state, action) => {
        state.krookiePlots = state.krookiePlots.map((plot) => {
          if (action.payload.has(plot.objectId)) {
            const geometry = action.payload.get(plot.objectId)!;
            return {
              ...plot,
              newGeometry: geometry,
              stringGeometry: convertPolygonToString(geometry),
              area: planarArea(geometry, "square-meters").toFixed(2),
            };
          }
          return plot;
        });
      })
      .addCase(fetchCoordinatesData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCoordinatesData.fulfilled, (state, action) => {
        state.coordinatesData = action.payload.normalizedData;
        state.loading = false;
      })
      .addCase(fetchCoordinatesData.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
  },
});

export const selectFilteredData = (state: RootState) => {
  const { data, selectedCity } = state.masterPlanModification;

  if (!selectedCity) {
    return data;
  }

  return data.filter((item) => item.estateEn === selectedCity);
};

export const {
  setSelectedFormationType,
  setFilter,
  setDrafter,
  setSelectedPlots,
  setGeometries,
  setSelectedCity,
  clearAllSelected,
  setFormDataEditDetails,
  setScreenshot,
  clearScreenshot,
  setResetKrookieData,
  setLogComment,
  updateConvertedKrookieData,
  resetFormDataEditDetails,
  setKrookiePlots,
  resetNewGeometry,
  setBlockNavigation,
  setCoordinatesData,
  deletePlot,
} = masterPlanModificationSlice.actions;

export default masterPlanModificationSlice.reducer;
